// DB graph — typed column / relation references over the generated Database.
//
// The *types* come from `database.types.ts` (Supabase typegen); the *runtime
// objects* are synthesized by a Proxy. You write `db.price_lists.company_id`
// and get a typed `ColumnRef` object (not a string) — a typo is a compile error.
//
// This is the single place schemas/DAOs/filters reference DB structure. No
// hand-written column-name strings, no hand-maintained FK map: the FK target is
// derived from the generated `Relationships`.

import type { Database } from "./database.types";

// ─── Base aliases over the generated schema ────────────────────────────────
//
// The graph's READ surface covers base tables AND views: a presentation field
// can bind a view-only computed column (e.g. `db.transactions_with_checks.
// margin_pln`) exactly like a base column. Writes (Insert/Update) remain
// table-only — views aren't writable.

export type Tables    = Database["public"]["Tables"];
export type Views     = Database["public"]["Views"];
/** Every relation exposing a `Row` — base tables + views (the read surface). */
type Relations        = Tables & Views;
export type TableName  = keyof Relations & string;
/** Base-table names only (the write surface). */
export type WritableTableName = keyof Tables & string;

export type RowOf<T extends TableName>    = Relations[T]["Row"];
export type InsertOf<T extends WritableTableName> = Tables[T]["Insert"];
export type UpdateOf<T extends WritableTableName> = Tables[T]["Update"];
export type ColumnOf<T extends TableName> = keyof RowOf<T> & string;

// ─── ColumnRef — a typed reference to one column ───────────────────────────

export interface ColumnRef<T extends TableName, C extends ColumnOf<T>, V> {
  readonly table: T;
  readonly column: C;
  /** Phantom — never set at runtime; carries the column's TS type for consumers. */
  readonly __v?: V;
}

// Convenience: any column ref of a table (column name widened to `string`, value
// type erased to `unknown`). Structurally a supertype of every `ColumnRef<T,C,V>`,
// so bindings stay assignable while consumers read `.column` as a real `string`.
export type AnyColumnRef<T extends TableName = TableName> = {
  readonly table: T;
  readonly column: string;
  readonly __v?: unknown;
};

// ─── FK relations ──────────────────────────────────────────────────────────
//
// A single FK column lists several `referencedRelation`s in the generated
// `Relationships` (the base table + every view exposing the column). Narrow to
// the real *table* target with `& TableName`.

export type RefOf<T extends TableName, C extends ColumnOf<T>> = Extract<
  Relations[T]["Relationships"][number],
  { columns: readonly [C] }
>["referencedRelation"] &
  TableName;

export interface FkRef<T extends TableName, C extends ColumnOf<T>>
  extends ColumnRef<T, C, number> {
  /** Phantom — the referenced base table, derived from `Relationships`. */
  readonly __ref?: RefOf<T, C>;
}

/**
 * Mark a (FK) column ref as a relation. Runtime is a no-op passthrough; the
 * type gains `__ref` (the target table). PostgREST infers the actual target
 * table from the FK column at query time, so no target string is needed here.
 */
export const rel = <T extends TableName, C extends ColumnOf<T>>(
  c: ColumnRef<T, C, RowOf<T>[C]>,
): FkRef<T, C> => c as FkRef<T, C>;

// ─── The `db` graph ────────────────────────────────────────────────────────

type DbGraph = {
  [T in TableName]: { [C in ColumnOf<T>]: ColumnRef<T, C, RowOf<T>[C]> };
};

/**
 * `db.<table>.<column>` → a typed `ColumnRef` object. Built lazily by a Proxy
 * so every (table, column) the generated `Database` declares is available and
 * type-checked, with zero hand-maintained entries.
 *
 *   db.price_lists.amount       // ColumnRef<"price_lists","amount", number>
 *   db.price_lists.nope         // compile error
 *   rel(db.price_lists.company_id) // FkRef → "companies"
 */
export const db: DbGraph = new Proxy({} as DbGraph, {
  get(_t, table: string) {
    return new Proxy(
      {},
      { get: (_c, column: string) => ({ table, column }) },
    );
  },
});
